import React, { useState } from 'react';
import { useOffline } from '../hooks/useOffline';
import './OfflineStatus.css';

function OfflineStatus({ token, showDetails = false }) {
  const { isOnline, syncStatus, triggerSync, getStorageInfo } = useOffline(token);
  const [showSyncDetails, setShowSyncDetails] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);

  const handleSyncClick = async () => {
    if (syncStatus.canSync) {
      await triggerSync();
    }
  };

  const toggleSyncDetails = () => {
    if (!showSyncDetails) {
      setStorageInfo(getStorageInfo());
    }
    setShowSyncDetails(!showSyncDetails);
  };

  const getStatusIcon = () => {
    if (syncStatus.isSyncing) {
      return '🔄';
    } else if (!isOnline) {
      return '📱';
    } else if (syncStatus.unsyncedCount > 0) {
      return '📤';
    } else {
      return '☁️';
    }
  };

  const getStatusText = () => {
    if (syncStatus.isSyncing) {
      return 'Syncing...';
    } else if (!isOnline) {
      return 'Offline Mode';
    } else if (syncStatus.failedCount > 0) {
      return `${syncStatus.failedCount} match${syncStatus.failedCount === 1 ? '' : 'es'} failed to sync`;
    } else if (syncStatus.unsyncedCount > 0) {
      return `${syncStatus.unsyncedCount} match${syncStatus.unsyncedCount === 1 ? '' : 'es'} to sync`;
    } else {
      return 'All synced';
    }
  };

  const getStatusClass = () => {
    if (syncStatus.isSyncing) {
      return 'syncing';
    } else if (!isOnline) {
      return 'offline';
    } else if (syncStatus.failedCount > 0) {
      return 'failed';
    } else if (syncStatus.unsyncedCount > 0) {
      return 'pending';
    } else {
      return 'online';
    }
  };

  if (!showDetails && isOnline && syncStatus.unsyncedCount === 0 && !syncStatus.isSyncing) {
    // Hide when everything is normal and not showing details
    return null;
  }

  return (
    <div className={`offline-status ${getStatusClass()}`}>
      <div className="status-main" onClick={showDetails ? toggleSyncDetails : undefined}>
        <span className="status-icon">{getStatusIcon()}</span>
        <span className="status-text">{getStatusText()}</span>
        
        {syncStatus.canSync && (
          <button 
            className="sync-button"
            onClick={handleSyncClick}
            disabled={syncStatus.isSyncing}
            title="Sync offline matches"
          >
            Sync
          </button>
        )}
        
        {showDetails && (
          <button 
            className="details-toggle"
            onClick={toggleSyncDetails}
            title={showSyncDetails ? "Hide details" : "Show details"}
          >
            {showSyncDetails ? '▼' : '▶'}
          </button>
        )}
      </div>

      {syncStatus.lastSyncMessage && (
        <div className="sync-message">
          {syncStatus.lastSyncMessage}
        </div>
      )}

      {syncStatus.syncError && (
        <div className="sync-error">
          Error: {syncStatus.syncError}
        </div>
      )}

      {showSyncDetails && showDetails && (
        <div className="sync-details">
          <div className="sync-details-header">Offline Storage Details</div>
          
          <div className="sync-detail-item">
            <span>Connection:</span>
            <span className={isOnline ? 'online' : 'offline'}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          
          <div className="sync-detail-item">
            <span>Unsynced matches:</span>
            <span>{syncStatus.unsyncedCount}</span>
          </div>

          {syncStatus.failedCount > 0 && (
            <div className="sync-detail-item">
              <span>Failed matches:</span>
              <span className="offline">{syncStatus.failedCount}</span>
            </div>
          )}

          {storageInfo && (
            <>
              <div className="sync-detail-item">
                <span>Total stored matches:</span>
                <span>{storageInfo.totalMatches}</span>
              </div>
              
              <div className="sync-detail-item">
                <span>Storage used:</span>
                <span>{storageInfo.storageSize}</span>
              </div>
            </>
          )}
          
          {syncStatus.syncErrors && syncStatus.syncErrors.length > 0 && (
            <div className="sync-errors">
              <div className="sync-errors-header">Recent Sync Errors:</div>
              {syncStatus.syncErrors.map((error, index) => (
                <div key={index} className="sync-error-item">
                  <span className="error-match">{error.match}:</span>
                  <span className="error-message">{error.error}</span>
                </div>
              ))}
            </div>
          )}
          
          <div className="sync-actions">
            {syncStatus.canSync && (
              <button 
                className="sync-action-button"
                onClick={handleSyncClick}
                disabled={syncStatus.isSyncing}
              >
                {syncStatus.isSyncing ? 'Syncing...' : 'Sync Now'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OfflineStatus;
